import {
  Anchor,
  Box,
  Button,
  ButtonGroup,
  Heading,
  Icon,
  Modal,
  Paragraph,
  TextButton,
} from '@zillow/constellation-10';
import { IconWarningFilled } from '@zillow/constellation-icons';
import { COMET_1, getCurrentEnvForClientSide, STAGING } from '@zg-rentals/rental-platform-config';

export type PersonalContactDetailsWarningData = {
  title?: string;
  text?: string;
  linkText?: string;
  linkUrl?: string;
  primaryButtonLabel?: string;
  primaryButtonType?: 'Button' | 'TextButton';
  secondaryButtonLabel?: string;
  secondaryButtonType?: 'Button' | 'TextButton';
};

export type PersonalContactDetailsWarningModalProps = {
  isOpen?: boolean;
  onGoBackClick(): void;
  onSendAnywayClick(): void;
  warningData?: PersonalContactDetailsWarningData;
  fallbackLinkUrl?: string;
};

export default function PersonalContactDetailsWarningModal({
  isOpen = true,
  onGoBackClick,
  onSendAnywayClick,
  warningData,
  fallbackLinkUrl = 'https://www.zillow.com/c/info/beware-of-scams-and-other-internet-fraud/',
}: PersonalContactDetailsWarningModalProps) {
  const handleGoBackClick = () => {
    onGoBackClick();
  };

  const handleSendAnywayClick = () => {
    onSendAnywayClick();
  };

  // Fallback values for when warningData is not available
  const title = warningData?.title || 'Be careful when sharing contact info';
  const text =
    warningData?.text ||
    "For your protection and to reduce spam, don't share your personal contact information unless you're confident you know who you're talking to.";
  const linkText = warningData?.linkText || 'Learn more';

  const getLinkUrl = () => {
    const env = getCurrentEnvForClientSide();
    const isNonProdEnvironment = env === COMET_1 || env === STAGING;

    if (isNonProdEnvironment) {
      return 'https://www.qa.zillow.net/z/info/beware-of-scams-and-other-internet-fraud/';
    }

    // For production/dev, use backend URL if provided, otherwise use the provided fallback URL
    return warningData?.linkUrl || fallbackLinkUrl;
  };

  const linkUrl = getLinkUrl();
  const primaryButtonLabel = warningData?.primaryButtonLabel || 'Go back';
  const primaryButtonType = warningData?.primaryButtonType || 'Button';
  const secondaryButtonLabel = warningData?.secondaryButtonLabel || 'Send anyway';
  const secondaryButtonType = warningData?.secondaryButtonType || 'TextButton';

  const renderButton = (type: 'Button' | 'TextButton', label: string, onClick: () => void, isPrimary = false) => {
    const commonProps = {
      tone: 'brand' as const,
      onClick,
      children: label,
    };

    if (type === 'TextButton') {
      return <TextButton {...commonProps} />;
    } else {
      return <Button {...commonProps} {...(isPrimary && { emphasis: 'filled' as const })} />;
    }
  };

  return (
    <Modal
      open={isOpen}
      onOpenChange={(open) => !open && handleGoBackClick()}
      dividers={false}
      size={{
        base: 'md',
        md: 'sm',
      }}
      body={
        <>
          <Box css={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <Icon size="xl" css={{ color: 'bg.feedback.warning.hero' }}>
              <IconWarningFilled />
            </Icon>
            <Heading level={3} textStyle="heading-sm" css={{ paddingTop: 'default' }}>
              {title}
            </Heading>
          </Box>
          <Paragraph css={{ paddingTop: 'default' }}>
            {text}{' '}
            <Anchor href={linkUrl} target="_blank">
              {linkText}
            </Anchor>
          </Paragraph>
        </>
      }
      footer={
        <ButtonGroup aria-label="personal-contact-details-warning-modal-options">
          {renderButton(secondaryButtonType, secondaryButtonLabel, handleSendAnywayClick)}
          {renderButton(primaryButtonType, primaryButtonLabel, handleGoBackClick, true)}
        </ButtonGroup>
      }
    />
  );
}
